import React, { useState, useEffect } from "react";
import { X, Check, AlertCircle, ShoppingBag, User, Phone, Wrench, Calendar, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PRESET_SERVICES = [
  "Химчистка стандарт",
  "Глубокая чистка + антибактериальная обработка",
  "Реставрация и покраска кожи",
  "Локальная подкраска потертостей",
  "Замена набоек / ремонт подошвы",
  "Водоотталкивающая нано-пропитка",
  "Глассаж мысков"
];

const MASTERS = ["Катя", "Женя", "Александр", "Пётр"];

export default function OrderModal({ isOpen, onClose, onSuccess, initialLead }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
    client_phone: "",
    brand_item: "",
    selectedServices: ["Химчистка стандарт"],
    customService: "",
    price: 2500,
    prepayment: 1000,
    shelf: "A1",
    urgency: "Стандартный",
    master: "Катя",
    notes: ""
  });

  useEffect(() => {
    if (initialLead) {
      setFormData((prev) => ({
        ...prev,
        client_name: initialLead.name || "",
        client_phone: initialLead.phone || "",
        brand_item: initialLead.comment || "Обувь",
        selectedServices: initialLead.service ? [initialLead.service] : ["Химчистка стандарт"],
        notes: `Заявка с сайта: ${initialLead.comment || ""}`
      }));
    }
  }, [initialLead]);

  if (!isOpen) return null;

  const toggleService = (srv) => {
    setFormData((prev) => {
      const exists = prev.selectedServices.includes(srv);
      if (exists) {
        return { ...prev, selectedServices: prev.selectedServices.filter((s) => s !== srv) };
      } else {
        return { ...prev, selectedServices: [...prev.selectedServices, srv] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_phone || !formData.brand_item) {
      toast.error("Укажите телефон клиента и модель обуви");
      return;
    }

    setLoading(true);
    try {
      const allServices = [...formData.selectedServices];
      if (formData.customService.trim()) {
        allServices.push(formData.customService.trim());
      }

      const payload = {
        client_name: formData.client_name.trim() || "Клиент",
        client_phone: formData.client_phone.trim(),
        brand_item: formData.brand_item.trim(),
        services: allServices.length > 0 ? allServices.join(", ") : "Химчистка стандарт",
        shelf: formData.shelf.trim() || "A1",
        price: Number(formData.price) || 0,
        cost: Math.round((Number(formData.price) || 0) * 0.15),
        prepayment: Number(formData.prepayment) || 0,
        urgency: formData.urgency,
        master: formData.master,
        notes: formData.notes.trim()
      };

      // Create order in CRM backend
      const res = await axios.post("/crm/api/orders", payload);

      // If tied to a lead from website, mark lead as in_progress
      if (initialLead?.id) {
        try {
          await api.patch(`/admin/leads/${initialLead.id}/status`, { status: "in_progress" });
        } catch (err) {
          console.warn("Could not update lead status", err);
        }
      }

      toast.success(`Заказ #${res.data.id || ""} успешно принят в работу!`);
      if (onSuccess) onSuccess(res.data);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Ошибка при сохранении заказа. Проверьте данные.");
    } finally {
      setLoading(false);
    }
  };

  const remainingDebt = Math.max(0, (Number(formData.price) || 0) - (Number(formData.prepayment) || 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-cream-paper border-2 border-burgundy shadow-2xl my-8 overflow-hidden">
        {/* Modal Header */}
        <div className="bg-burgundy text-cream px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShoppingBag size={20} className="text-gold" />
            <h3 className="font-heading text-xl font-bold tracking-wide">
              {initialLead ? "Оформление заказа из заявки" : "Приемка обуви в цех"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-cream/20 text-cream transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5 flex items-center gap-1.5">
                <Phone size={13} /> Телефон клиента *
              </label>
              <input
                type="tel"
                required
                placeholder="+7 (999) 000-00-00"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3.5 py-2.5 text-sm font-medium outline-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5 flex items-center gap-1.5">
                <User size={13} /> Имя клиента
              </label>
              <input
                type="text"
                placeholder="Константин"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3.5 py-2.5 text-sm font-medium outline-none"
              />
            </div>
          </div>

          {/* Shoe Item & Shelf */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5">
                Модель / Бренд обуви *
              </label>
              <input
                type="text"
                required
                placeholder="Loro Piana замша синяя, 43 р."
                value={formData.brand_item}
                onChange={(e) => setFormData({ ...formData, brand_item: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3.5 py-2.5 text-sm font-medium outline-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5">
                Полка хранения
              </label>
              <input
                type="text"
                placeholder="A-12"
                value={formData.shelf}
                onChange={(e) => setFormData({ ...formData, shelf: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3.5 py-2.5 text-sm font-medium outline-none"
              />
            </div>
          </div>

          {/* Services Multiselect */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-2 flex items-center gap-1.5">
              <Wrench size={13} /> Выбранные услуги
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PRESET_SERVICES.map((srv) => {
                const selected = formData.selectedServices.includes(srv);
                return (
                  <button
                    type="button"
                    key={srv}
                    onClick={() => toggleService(srv)}
                    className={`flex items-center justify-between text-left px-3 py-2 text-xs border transition-all ${
                      selected
                        ? "bg-burgundy text-cream border-burgundy font-bold shadow-sm"
                        : "bg-cream text-ink border-burgundy/30 hover:border-burgundy"
                    }`}
                  >
                    <span>{srv}</span>
                    {selected && <Check size={14} className="text-gold shrink-0 ml-1.5" />}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              placeholder="+ Другая услуга (вручную)"
              value={formData.customService}
              onChange={(e) => setFormData({ ...formData, customService: e.target.value })}
              className="mt-2 w-full bg-cream border border-burgundy/40 focus:border-burgundy px-3 py-1.5 text-xs outline-none"
            />
          </div>

          {/* Master & Urgency */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5">
                Назначенный мастер
              </label>
              <select
                value={formData.master}
                onChange={(e) => setFormData({ ...formData, master: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3 py-2 text-sm font-medium outline-none cursor-pointer"
              >
                {MASTERS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5 flex items-center gap-1.5">
                <Calendar size={13} /> Срочность
              </label>
              <select
                value={formData.urgency}
                onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy px-3 py-2 text-sm font-medium outline-none cursor-pointer"
              >
                <option value="Стандартный">Стандартный (3–5 дней)</option>
                <option value="Срочный">Срочный (24 часа, +30%)</option>
                <option value="Экспресс">Экспресс (в тот же день)</option>
              </select>
            </div>
          </div>

          {/* Finances for order (Price & Prepayment) */}
          <div className="bg-cream/50 border-2 border-burgundy/30 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1">
                  Стоимость заказа (₽)
                </label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full bg-cream-paper border-2 border-burgundy/60 focus:border-burgundy px-3 py-2 text-base font-bold text-burgundy outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1">
                  Внесенная предоплата (₽)
                </label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={formData.prepayment}
                  onChange={(e) => setFormData({ ...formData, prepayment: e.target.value })}
                  className="w-full bg-cream-paper border-2 border-burgundy/60 focus:border-burgundy px-3 py-2 text-base font-bold text-emerald-800 outline-none"
                />
              </div>
              <div className="text-right">
                <span className="text-[10px] uppercase tracking-wider font-bold text-muted_ink block">
                  К доплате при выдаче
                </span>
                <span className={`text-xl font-heading font-bold ${remainingDebt > 0 ? "text-amber-800" : "text-emerald-700"}`}>
                  {remainingDebt.toLocaleString("ru-RU")} ₽
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-muted_ink mb-1.5">
              Заметки / Особенности / Дефекты
            </label>
            <textarea
              rows="2"
              placeholder="Стерта набойка на левом каблуке, глубокая царапина на мыске..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full bg-cream border-2 border-burgundy/40 focus:border-burgundy p-2.5 text-xs font-medium outline-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-3 border-t border-burgundy/20">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-muted_ink hover:text-ink transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gold hover:bg-gold-light text-burgundy-ink font-bold text-sm border-2 border-burgundy shadow-md transition-all active:scale-95 disabled:opacity-50"
            >
              <Sparkles size={16} />
              <span>{loading ? "Сохраняем…" : "Принять и отправить в работу"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
